import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { useUpdateQuotas } from "@/hooks/useTenantQueries";
import { TenantQuotas } from "@/services/admin-tenant.service";
import { useCloseGuard } from "@/hooks/use-close-guard";

// ============================================================================
// Validation Schema
// ============================================================================

const quotaSchema = z.object({
  maxVMs: z.coerce.number().min(1, "Must be at least 1").max(1000),
  maxStorage: z.coerce.number().min(10, "Must be at least 10 GB").max(100000),
  maxNetwork: z.coerce.number().min(10, "Must be at least 10 GB").max(100000),
});

type QuotaFormData = z.infer<typeof quotaSchema>;

// ============================================================================
// Component
// ============================================================================

interface TenantQuotaEditorProps {
  tenantId: string;
  currentQuotas: TenantQuotas | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TenantQuotaEditor({
  tenantId,
  currentQuotas,
  open,
  onOpenChange,
}: TenantQuotaEditorProps) {
  const updateQuotas = useUpdateQuotas(tenantId);

  const form = useForm<QuotaFormData>({
    resolver: zodResolver(quotaSchema),
    defaultValues: {
      maxVMs: currentQuotas?.maxVMs || 10,
      maxStorage: currentQuotas?.maxStorage || 500,
      maxNetwork: currentQuotas?.maxNetwork || 1000,
    },
  });

  // Reset form when quotas change or dialog opens
  React.useEffect(() => {
    if (currentQuotas && open) {
      form.reset({
        maxVMs: currentQuotas.maxVMs,
        maxStorage: currentQuotas.maxStorage,
        maxNetwork: currentQuotas.maxNetwork,
      });
    }
  }, [currentQuotas, open, form]);

  const onSubmit = async (data: QuotaFormData) => {
    try {
      await updateQuotas.mutateAsync(data);
      onOpenChange(false);
    } catch {
      // Error handled by mutation
    }
  };

  const guard = useCloseGuard({
    open,
    onOpenChange,
    isDirty: form.formState.isDirty,
    isSubmitting: updateQuotas.isPending,
    onDiscard: () => form.reset(),
  });

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Resource Quotas</DialogTitle>
          </DialogHeader>

          <DialogBody withoutFooter className="space-y-4">
            <DialogDescription>
              Adjust the maximum resource limits for this tenant
            </DialogDescription>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <fieldset
                  disabled={updateQuotas.isPending}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="maxVMs"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Virtual Machines</FormLabel>
                        <FormControl>
                          <Input type="number" min={1} max={1000} {...field} />
                        </FormControl>
                        <FormDescription>
                          Maximum number of VMs this tenant can create
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="maxStorage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Storage (GB)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={10}
                            max={100000}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Maximum storage allocation in gigabytes
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="maxNetwork"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Network Bandwidth (GB/month)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={10}
                            max={100000}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Maximum monthly network transfer in gigabytes
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => guard.onOpenChange(false)}
                      disabled={updateQuotas.isPending}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={updateQuotas.isPending}>
                      {updateQuotas.isPending && <Spinner className="mr-2" />}
                      {updateQuotas.isPending ? "Saving..." : "Save Quotas"}
                    </Button>
                  </DialogFooter>
                </fieldset>
              </form>
            </Form>
          </DialogBody>
        </DialogContent>
      </Dialog>
      {guard.dialog}
    </>
  );
}
